import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  Toggle,
  columnTypes,
  isExcludedByCurrencyOrBasePairFilters,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useSecuritiesContext,
  useWSFilterPipe,
  useWsBlotterTable,
  type Leaves,
  type Position,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';

import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useFeatureFlag } from '@src/hooks';
import { BLOTTER_TABLE_FILTERS_CONTAINER_ID, DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS } from '@talos/kyoko';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { defaultDerivativesBlotterColumns, defaultDerivativesBlotterFilter } from '../defaults';
import { getPositionsBlotterRowAsset, getPositionsBlotterRowKey } from '../positionBlotterRow';
import { GROUP_COL_WIDTH } from '../tokens';
import type { ClonePositionsTabFunc, DerivativesPositionsTabProps } from '../types';
import { usePositionsBaseRequest } from '../usePositionsBaseRequest';
import { usePositionsExpiryFilter } from '../usePositionsExpiryFilter';
import { usePositionsMenu } from '../usePositionsMenu';
import { useShowAdjustMarketAccountPositionButton } from '../useShowAdjustMarketAccountPositionButton';
import { usePositionsDerivativesBlotterGrouping } from './usePositionsDerivativesBlotterGrouping';
import { usePositionsDerivativesColumns } from './usePositionsDerivativesColumns';
import { toDerivativesServerFilter, usePositionsDerivativesFilter } from './usePositionsDerivativesFilter';

export interface PositionsDerivativesBlotterParams {
  blotterID: string;
  initialIsOpen?: boolean;
  tab: DerivativesPositionsTabProps;
  onUpdateTab: (tab: DerivativesPositionsTabProps) => void;
  onCloneTab: ClonePositionsTabFunc;
  tag?: string;
}

const derivAggSpecs = [
  { valuePath: 'Equivalent.Delta', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.UnrealizedPnL', currencyPath: 'Equivalent.Currency' },
] satisfies { valuePath: Leaves<Position>; currencyPath: Leaves<Position> }[];

export function PositionsDerivativesBlotter({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  onCloneTab,
  tag,
}: PositionsDerivativesBlotterParams) {
  const mixpanel = useMixpanel();
  const { enableLegacyUpdateBalances } = useFeatureFlag();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultDerivativesBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultDerivativesBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;

  const defaultColumns = usePositionsDerivativesColumns(defaultBlotterColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
    extraGridState: {
      rowGroupExpansion: {
        expansionState: 'all-expanded',
        expansionExceptions: [],
      },
    },
  });
  const filterResults = usePositionsDerivativesFilter({
    persistedBlotterTable,
    defaultFilter,
  });

  const { filterBuilderProps } = filterResults;

  // Stable ref to latest grouping handler; defined after `useWsBlotterTable` (see `usePositionsDerivativesBlotterGrouping`).
  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    tabType: 'Derivatives',
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(
    () => [...defaultColumns, ...positionsMenu.columns],
    [defaultColumns, positionsMenu.columns]
  );

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getPositionsBlotterRowKey,
    aggSpecs: derivAggSpecs,
  });

  const { securitiesBySymbol } = useSecuritiesContext();
  const expiryFilter = usePositionsExpiryFilter(getPositionsBlotterRowAsset, filterResults.filter.Expiries);
  const filterFunc = useCallback(
    (row: Position) => {
      if (!expiryFilter(row)) {
        return false;
      }
      if (
        isExcludedByCurrencyOrBasePairFilters(
          getPositionsBlotterRowAsset(row),
          securitiesBySymbol,
          filterResults.filter.Currencies,
          filterResults.filter.BasePairs
        )
      ) {
        return false;
      }
      return true;
    },
    [expiryFilter, securitiesBySymbol, filterResults.filter.Currencies, filterResults.filter.BasePairs]
  );
  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionsBlotterRowKey, filterFunc });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        width: GROUP_COL_WIDTH,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const baseRequest = usePositionsBaseRequest({ blotterID, showZeroBalances });

  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(toDerivativesServerFilter(filterResults.filter, filterResults.derivativesProductTypes)),
    }),
    [baseRequest, filterResults.derivativesProductTypes, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    pipe: filterPipe,
    pinnedRowDataPipe: aggPipe,
    rowID: 'rowID',
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems: positionsMenu.getExtraMainMenuItems,
    contextMenuOptions: positionsMenu.contextMenuOptions,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      autoGroupColumnDef,
      groupDisplayType: 'singleColumn',
    },
    exportOptions: {
      name: 'PositionsDerivatives',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onGroupingStateUpdated, groupingToggleButtons } = usePositionsDerivativesBlotterGrouping({
    setRowGroupColumns,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const showAdjustMarketAccountPositionButton = useShowAdjustMarketAccountPositionButton();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={groupingToggleButtons}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} data-testid="collapse-all-button" />
          </>
        }
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Divider orientation="horizontal" />
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
              {showAdjustMarketAccountPositionButton && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  data-testid="update-balances-button"
                  onClick={() => {
                    positionsMenu.openUpdateBalancesDialog();
                  }}
                >
                  {enableLegacyUpdateBalances ? 'Update Balances' : 'Adjust Market Account Position'}
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
    </>
  );
}
